import React, { useState, useEffect } from 'react';
import moment from 'moment';
import APP_COLOURS from '../../../config/colors';
import { staffAPI } from '../../../config/api';

const StaffChangeLogs = ({ staffId, staffName }) => {
  const [loading, setLoading] = useState(true);
  const [changeLogs, setChangeLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchChangeLogs();
  }, [staffId, filter]);

  const fetchChangeLogs = async () => {
    if (!staffId) return;
    
    setLoading(true);
    try {
      const params = {
        limit: 100,
      };
      
      if (filter !== 'all') {
        params.changeType = filter;
      }

      const data = await staffAPI.getStaffChangeLogs(staffId, params);
      
      setChangeLogs(data.changeLogs || []);
      setSummary(data.summary || null);
    } catch (error) {
      console.error('Error fetching change logs:', error);
      setChangeLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeIcon = (changeType) => {
    switch (changeType) {
      case 'SALE_UPDATE':
        return 'fa-edit';
      case 'STAFF_STATUS_UPDATE':
        return 'fa-user-check';
      case 'STAFF_PROFILE_UPDATE':
        return 'fa-user-edit';
      default:
        return 'fa-history';
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'SALE_UPDATE':
        return APP_COLOURS.BLUE;
      case 'STAFF_STATUS_UPDATE':
        return APP_COLOURS.ORANGE;
      case 'STAFF_PROFILE_UPDATE':
        return APP_COLOURS.GREEN;
      default:
        return APP_COLOURS.TEXTCOLOR;
    }
  };

  const getChangeTypeLabel = (changeType) => {
    switch (changeType) {
      case 'SALE_UPDATE':
        return 'Sale Modified';
      case 'STAFF_STATUS_UPDATE':
        return 'Status Changed';
      case 'STAFF_PROFILE_UPDATE':
        return 'Profile Updated';
      default:
        return 'Unknown Change';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'UPDATE_DATE':
        return 'Date Changed';
      case 'REMOVE_ITEM':
        return 'Item Removed';
      case 'UPDATE_ITEM_PRICE':
        return 'Price Updated';
      case 'CREDIT_ITEM':
        return 'Return Credited';
      case 'UPDATE_STATUS':
        return 'Status Updated';
      default:
        return action;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
        <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading change logs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Filters */}
      <div className="rounded-lg p-6" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              <i className="fas fa-history mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
              Change History
            </h3>
            <p className="text-sm mt-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              All modifications made to {staffName}'s profile and sales data
            </p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium mr-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>Filter:</span>
          {[
            { value: 'all', label: 'All Changes', icon: 'fa-list' },
            { value: 'SALE_UPDATE', label: 'Sales', icon: 'fa-shopping-cart' },
            { value: 'STAFF_STATUS_UPDATE', label: 'Status', icon: 'fa-user-check' },
            { value: 'STAFF_PROFILE_UPDATE', label: 'Profile', icon: 'fa-user-edit' },
          ].map((filterOption) => (
            <button
              key={filterOption.value}
              onClick={() => setFilter(filterOption.value)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              style={{
                backgroundColor: filter === filterOption.value ? APP_COLOURS.BLUE : APP_COLOURS.BG,
                color: filter === filterOption.value ? '#ffffff' : APP_COLOURS.TEXTCOLOR,
                border: `1px solid ${filter === filterOption.value ? APP_COLOURS.BLUE : APP_COLOURS.BG4}`,
              }}
            >
              <i className={`fas ${filterOption.icon} mr-2`}></i>
              {filterOption.label}
            </button>
          ))}
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>{summary.total}</p>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Changes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                {summary.byType['SALE_UPDATE'] || 0}
              </p>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Sale Updates</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: APP_COLOURS.ORANGE }}>
                {summary.byType['STAFF_STATUS_UPDATE'] || 0}
              </p>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Status Changes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: APP_COLOURS.GREEN }}>
                {summary.byType['STAFF_PROFILE_UPDATE'] || 0}
              </p>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Profile Updates</p>
            </div>
          </div>
        )}
      </div>

      {/* Change Logs Timeline */}
      <div className="space-y-3">
        {changeLogs.length > 0 ? (
          changeLogs.map((log, index) => (
            <div
              key={log.id || index}
              className="rounded-lg overflow-hidden transition-all duration-200"
              style={{ 
                backgroundColor: APP_COLOURS.BG3, 
                border: `1px solid ${expandedLog === index ? APP_COLOURS.BLUE : APP_COLOURS.BG4}`,
              }}
            >
              {/* Log Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                onMouseEnter={(e) => {
                  if (expandedLog !== index) {
                    e.currentTarget.style.backgroundColor = APP_COLOURS.BG4 + '40';
                  }
                }}
                onMouseLeave={(e) => {
                  if (expandedLog !== index) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    {/* Icon */}
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-full mr-4 flex-shrink-0"
                      style={{ backgroundColor: getChangeTypeColor(log.changeType) + '20' }}
                    >
                      <i 
                        className={`fas ${getChangeTypeIcon(log.changeType)} text-lg`} 
                        style={{ color: getChangeTypeColor(log.changeType) }}
                      ></i>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded"
                          style={{ 
                            backgroundColor: getChangeTypeColor(log.changeType) + '20',
                            color: getChangeTypeColor(log.changeType),
                          }}
                        >
                          {getChangeTypeLabel(log.changeType)}
                        </span>
                        {log.action && (
                          <span 
                            className="inline-flex px-2 py-1 text-xs font-medium rounded"
                            style={{ 
                              backgroundColor: APP_COLOURS.BG4,
                              color: APP_COLOURS.TEXTCOLOR,
                            }}
                          >
                            {getActionLabel(log.action)}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm font-medium mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {log.changes?.description || 'No description available'}
                      </p>

                      <div className="flex items-center gap-4 text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        <span>
                          <i className="fas fa-user mr-1"></i>
                          {log.changedByName || 'Unknown'}
                        </span>
                        <span>
                          <i className="fas fa-clock mr-1"></i>
                          {moment(log.timestamp).format('DD MMM YYYY, h:mm A')}
                        </span>
                        <span>
                          <i className="fas fa-history mr-1"></i>
                          {moment(log.timestamp).fromNow()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expand Icon */}
                  <button className="ml-4 text-lg" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                    <i className={`fas fa-chevron-${expandedLog === index ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedLog === index && (
                <div className="p-4 pt-0">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG }}>
                    <h4 className="text-sm font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      Change Details
                    </h4>

                    {/* Change Type Specific Details */}
                    {log.changeType === 'SALE_UPDATE' && (
                      <div className="space-y-3">
                        {log.entityId && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Sale ID:</span>
                            <span className="font-mono text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>{log.entityId}</span>
                          </div>
                        )}

                        {log.changes?.field && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Field Modified:</span>
                            <span style={{ color: APP_COLOURS.TEXTCOLOR }}>{log.changes.field}</span>
                          </div>
                        )}

                        {log.changes?.itemName && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Item:</span>
                            <span style={{ color: APP_COLOURS.TEXTCOLOR }}>{log.changes.itemName}</span>
                          </div>
                        )}

                        {log.changes?.oldTotal !== undefined && log.changes?.newTotal !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Changed:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-red-500 line-through">
                                R{parseFloat(log.changes.oldTotal || 0).toFixed(2)}
                              </span>
                              <i className="fas fa-arrow-right text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
                              <span className="font-semibold" style={{ color: APP_COLOURS.GREEN }}>
                                R{parseFloat(log.changes.newTotal || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}

                        {log.changes?.oldPrice !== undefined && log.changes?.newPrice !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Price Changed:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-red-500 line-through">
                                R{parseFloat(log.changes.oldPrice || 0).toFixed(2)}
                              </span>
                              <i className="fas fa-arrow-right text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
                              <span className="font-semibold" style={{ color: APP_COLOURS.GREEN }}>
                                R{parseFloat(log.changes.newPrice || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {log.changeType === 'STAFF_STATUS_UPDATE' && (
                      <div className="space-y-3">
                        {log.changes?.oldValue && log.changes?.newValue && (
                          <div className="flex justify-between text-sm">
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Status Changed:</span>
                            <div className="flex items-center gap-2">
                              <span 
                                className="px-2 py-1 text-xs font-semibold rounded"
                                style={{ 
                                  backgroundColor: APP_COLOURS.RED + '20',
                                  color: APP_COLOURS.RED,
                                }}
                              >
                                {log.changes.oldValue}
                              </span>
                              <i className="fas fa-arrow-right text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
                              <span 
                                className="px-2 py-1 text-xs font-semibold rounded"
                                style={{ 
                                  backgroundColor: APP_COLOURS.GREEN + '20',
                                  color: APP_COLOURS.GREEN,
                                }}
                              >
                                {log.changes.newValue}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Common Details */}
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
                      <div className="flex justify-between text-xs mb-2">
                        <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Modified By:</span>
                        <span style={{ color: APP_COLOURS.TEXTCOLOR }}>{log.changedByName}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Timestamp:</span>
                        <span style={{ color: APP_COLOURS.TEXTCOLOR }}>
                          {moment(log.timestamp).format('MMMM Do YYYY, h:mm:ss A')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="p-12 text-center rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
            <i className="fas fa-clipboard-list text-5xl mb-4" style={{ color: APP_COLOURS.BG5 }}></i>
            <p className="text-lg" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              No change logs found
            </p>
            <p className="text-sm mt-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              {filter !== 'all' 
                ? 'Try changing the filter to see more results'
                : 'No modifications have been made yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffChangeLogs;

